import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Nav, Modal, Button, Form } from 'react-bootstrap';
import Select, { MultiValue } from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import { DeleteModal } from '../../Common/DeleteModal';
import { CommunityVideo, VideoCategory, VideoStatus } from '../../api/community/types';
import { communityService } from '../../api/community/communityService';
import { toast } from 'react-toastify';
import ActionCard from './components/ActionCard';
import LevelUpCard from './components/LevelUpCard';
import SocialCard from './components/SocialCard';
import AddNewCommunityForm from './components/AddNewCommunityForm';
import './CommunityCategoryPage.css';
import '../../pages/ManageTournaments/ManageTournaments.css';
import '../../pages/ManageTournaments/ManageTournaments.css';

const CommunityCategoryPage: React.FC = () => {
  const { category: categoryParam } = useParams<{ category: string }>();
  const navigate = useNavigate();
  
  // Map URL param to VideoCategory enum
  const getCategoryFromParam = (param: string | undefined): VideoCategory => {
    if (param === 'action') return VideoCategory.ACTION;
    if (param === 'level_up') return VideoCategory.LEVEL_UP;
    if (param === 'social') return VideoCategory.SOCIAL;
    return VideoCategory.ACTION; // Default
  };
  
  const category = getCategoryFromParam(categoryParam);
  const categoryLabel = category === VideoCategory.ACTION ? 'Action' : 
                        category === VideoCategory.LEVEL_UP ? 'Level Up' : 
                        'Social';
  
  // Debug: Log category to verify it's SOCIAL
  console.log('CommunityCategoryPage - category:', category, 'isSocial:', category === VideoCategory.SOCIAL);
  
  const [activeTab, setActiveTab] = useState<string>('live');
  const [videos, setVideos] = useState<CommunityVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<MultiValue<{ value: string; label: string }>>([]);
  const [availableTags, setAvailableTags] = useState<{ value: string; label: string }[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<CommunityVideo | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<CommunityVideo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [videoToPublish, setVideoToPublish] = useState<CommunityVideo | null>(null);
  const [videoToUnpublish, setVideoToUnpublish] = useState<CommunityVideo | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const limit = 20;
  
  // Track in-flight requests to prevent duplicate calls
  const fetchingRef = useRef(false);
  
  // Map tab to status
  const getStatusForTab = (tab: string): VideoStatus | undefined => {
    switch (tab) {
      case 'live':
        return VideoStatus.PUBLISHED;
      case 'archived':
        return VideoStatus.ARCHIVED;
      case 'scheduled':
        return VideoStatus.SCHEDULED;
      case 'draft':
        return VideoStatus.DRAFT;
      case 'rejected':
        return VideoStatus.REJECTED;
      default:
        return undefined;
    }
  };
  
  const fetchVideos = useCallback(async (pageNum: number = 1, forceRefresh: boolean = false) => {
    // Prevent duplicate requests
    if (fetchingRef.current) {
      console.log(`[${categoryLabel}] Request already in progress, skipping duplicate call`);
      return;
    }
    
    fetchingRef.current = true;
    try {
      setLoading(true);
      const status = getStatusForTab(activeTab);
      
      let response;
      
      if (category === VideoCategory.LEVEL_UP) {
        // For Level Up, use videos-level-up endpoint with separate mediaStatus and seriesStatus
        let mediaStatus: string | undefined;
        let seriesStatus: string | undefined;
        
        if (activeTab === 'live') {
          mediaStatus = 'published';
          seriesStatus = 'published';
        } else if (activeTab === 'draft') {
          // Draft series have draft mediaStatus and draft seriesStatus
          mediaStatus = 'draft';
          seriesStatus = 'draft';
        } else if (activeTab === 'archived') {
          mediaStatus = 'archived';
          seriesStatus = 'archived';
        } else {
          // For other tabs (scheduled, rejected), use the status as-is or default to published
          const levelUpStatus = status ? String(status) : 'published';
          mediaStatus = levelUpStatus;
          seriesStatus = levelUpStatus;
        }
        
        // Extract tag values from selected tags
        const tagValues = selectedTags.length > 0 
          ? selectedTags.map((tag: { value: string; label: string }) => tag.value)
          : undefined;
        
        // Use search query
        const searchQuery = debouncedSearchQuery.trim() || undefined;
        
        response = await communityService.getVideosLevelUp(
          pageNum,
          limit,
          undefined, // status (not used when mediaStatus/seriesStatus are provided)
          forceRefresh,
          searchQuery,
          tagValues,
          mediaStatus,
          seriesStatus
        );
      } else {
        // Extract tag values from selected tags
        const tagValues = selectedTags.length > 0 
          ? selectedTags.map((tag: { value: string; label: string }) => tag.value)
          : undefined;
        
        // Use unified /videos API for Action and Social categories
        // Backend handles all filtering: category, status, search, tags
        const searchQuery = debouncedSearchQuery.trim() || undefined;
        
        // Use unified /videos API for all categories
        // Backend supports: category, status, search, tags, page, limit, sortBy, sortOrder
        response = await communityService.getVideosByCategory(
          category,
          pageNum,
          limit,
          status,
          searchQuery,
          tagValues,
          undefined, // featured (not used)
          forceRefresh
        );
      }
      
      if (response?.data) {
        let videosData = response.data.videos || [];
        
        // For Level Up draft tab, ensure status is set to 'draft'
        // We're using mediaStatus=draft&seriesStatus=draft, so API should return draft status correctly
        // Keeping override as a safety measure to ensure correct badge display
        if (category === VideoCategory.LEVEL_UP && activeTab === 'draft') {
          videosData = videosData.map(video => ({
            ...video,
            status: 'draft' // Override status to draft for draft series
          }));
        }
        
        // For Level Up archived tab, ensure status is set to 'archived'
        // The API might return incorrect status, so we override it to 'archived'
        if (category === VideoCategory.LEVEL_UP && activeTab === 'archived') {
          videosData = videosData.map(video => ({
            ...video,
            status: 'archived' // Override status to archived for archived series
          }));
        }
        
        // Backend handles all filtering and pagination (same as player stats)
        // No frontend filtering needed
        setVideos(videosData);
        setTotalPages(response.data.totalPages || 1);
        setTotalCount(response.data.total || 0);
      } else {
        setVideos([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error(`Error fetching ${categoryLabel} videos:`, error);
      setVideos([]);
      toast.error('Failed to fetch videos. Please try again.');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [category, categoryLabel, activeTab, limit, debouncedSearchQuery, selectedTags]);
  
  // Fetch available tags on component mount
  useEffect(() => {
    const loadTags = async () => {
      setLoadingTags(true);
      try {
        const tags = await communityService.getTags();
        // Filter out empty, null, undefined tags and remove duplicates
        const uniqueTags = Array.from(new Set(
          tags
            .filter(tag => tag && typeof tag === 'string' && tag.trim().length > 0)
            .map(tag => tag.trim())
        ));
        // Sort tags alphabetically for better UX
        uniqueTags.sort((a, b) => a.localeCompare(b));
        // Map to react-select format
        const tagOptions = uniqueTags.map(tag => ({
          value: tag,
          label: tag
        }));
        setAvailableTags(tagOptions);
      } catch (error) {
        console.error('Error loading tags:', error);
        setAvailableTags([]);
      } finally {
        setLoadingTags(false);
      }
    };
    
    loadTags();
  }, []);

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Reset activeTab to 'live' if 'scheduled' or 'rejected' is active when switching to Level Up category
  useEffect(() => {
    if (category === VideoCategory.LEVEL_UP && (activeTab === 'scheduled' || activeTab === 'rejected')) {
      setActiveTab('live');
    }
  }, [category, activeTab]);

  // Fetch videos when category, tab, refreshKey, search query, or tags change
  useEffect(() => {
    setPage(1);
    setVideos([]);
    // Only use forceRefresh when refreshKey changes (user action), not on normal tab/category/search/tags changes
    const isRefresh = refreshKey > 0;
    fetchVideos(1, isRefresh);
  }, [category, activeTab, refreshKey, debouncedSearchQuery, selectedTags, fetchVideos]);
  
  const handlePageChange = (newPage: number) => {
    if (!loading && newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      fetchVideos(newPage, false);
    }
  };
  
  const handleToggleMenu = (videoId: string) => {
    setOpenMenuId(openMenuId === videoId ? null : videoId);
  };
  
  const handleCloseMenu = () => {
    setOpenMenuId(null);
  };
  
  const handleAddNew = () => {
    setShowAddForm(true);
  };
  
  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setEditingVideo(null);
  };
  
  const handleFormSuccess = () => {
    // For Level Up series/chapters, the form stays open to add more chapters
    // Only close the form if it's not Level Up
    if (category !== VideoCategory.LEVEL_UP) {
      setShowAddForm(false);
      setEditingVideo(null);
    }
    // Trigger refresh immediately without delay
    setRefreshKey(prev => prev + 1);
  };
  
  const handleEditVideo = (video: CommunityVideo) => {
    setEditingVideo(video);
    setShowAddForm(true);
  };
  
  const handleUnpublishVideo = (video: CommunityVideo) => {
    setVideoToUnpublish(video);
    setShowUnpublishModal(true);
  };

  const confirmUnpublishVideo = async () => {
    if (!videoToUnpublish) return;
    
    try {
      setIsUnpublishing(true);
      
      // For Level Up, use series unpublish API, otherwise use video reject API
      if (category === VideoCategory.LEVEL_UP && videoToUnpublish.videoType === 'series') {
        await communityService.unpublishSeries(videoToUnpublish._id);
        toast.success('Series unpublished successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.rejectVideo(videoToUnpublish._id);
        toast.success('Video unpublished successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      setShowUnpublishModal(false);
      setVideoToUnpublish(null);
      
      // Refresh videos list
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error unpublishing:', error);
      
      let errorMessage = category === VideoCategory.LEVEL_UP && videoToUnpublish.videoType === 'series' 
        ? 'Failed to unpublish series. Please try again.' 
        : 'Failed to unpublish video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsUnpublishing(false);
    }
  };

  const handlePublishVideo = (video: CommunityVideo) => {
    setVideoToPublish(video);
    setShowPublishModal(true);
  };

  const confirmPublishVideo = async () => {
    if (!videoToPublish) return;
    
    // Check if it's a Level Up series
    const isLevelUpSeries = category === VideoCategory.LEVEL_UP && 
      (videoToPublish.contentType === 'series' || videoToPublish.videoType === 'series');
    
    // For Level Up series, validate that it has chapters before publishing
    if (isLevelUpSeries) {
      const totalChapters = (videoToPublish as any).totalChapters || 0;
      if (totalChapters === 0) {
        toast.error('Cannot publish series without chapters. Please add at least one chapter before publishing.', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        setShowPublishModal(false);
        setVideoToPublish(null);
        return;
      }
    }
    
    try {
      setIsPublishing(true);
      
      // For Level Up, use series publish API, otherwise use video publish API
      if (isLevelUpSeries) {
        await communityService.publishSeries(videoToPublish._id);
        toast.success('Series published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.publishVideo(videoToPublish._id);
        toast.success('Video published successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      setShowPublishModal(false);
      setVideoToPublish(null);
      
      // Refresh videos list
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error('Error publishing:', error);
      
      let errorMessage = category === VideoCategory.LEVEL_UP && videoToPublish.videoType === 'series' 
        ? 'Failed to publish series. Please try again.' 
        : 'Failed to publish video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsPublishing(false);
    }
  };
  
  const handleDeleteVideo = (video: CommunityVideo) => {
    setVideoToDelete(video);
    setShowDeleteModal(true);
    setDeleteError(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setVideoToDelete(null);
    setDeleteError(null);
  };

  const confirmDeleteVideo = async () => {
    if (!videoToDelete) return;

    // Check if it's a Level Up series
    const isLevelUpSeries = videoToDelete.category === 'level_up' && videoToDelete.videoType === 'series';

    try {
      setIsDeleting(true);
      setDeleteError(null);
      
      if (isLevelUpSeries) {
        // For Level Up series, use archive API instead of delete
        await communityService.archiveSeries(videoToDelete._id);
        toast.success('Series archived successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      } else {
        await communityService.deleteVideo(videoToDelete._id);
        toast.success('Video deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
      
      // Close modal
      handleCloseDeleteModal();
      
      // Refresh videos
      setRefreshKey(prev => prev + 1);
    } catch (error: any) {
      console.error(`Error ${isLevelUpSeries ? 'archiving' : 'deleting'} ${isLevelUpSeries ? 'series' : 'video'}:`, error);
      
      // Extract error message
      let errorMessage = isLevelUpSeries 
        ? 'Failed to archive series. Please try again.'
        : 'Failed to delete video. Please try again.';
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setDeleteError(errorMessage);
      
      // Show error toast
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Empty state
  if (!loading && videos.length === 0) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <Breadcrumbs 
              items={[
                { label: 'Community', onClick: () => navigate('/community') },
                { label: categoryLabel, active: true }
              ]} 
            />
          </div>
          
          <div className="community-category-page">
            <div className="category-page-header">
              <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                <button 
                  className="back-button"
                  onClick={() => navigate('/community')}
                  style={{ position: 'relative', zIndex: 10 }}
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <h2 className="category-page-title" style={{ margin: 0 }}>{categoryLabel}</h2>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                {/* Search Bar */}
                <div style={{ position: 'relative', width: '250px' }}>
                  <Form.Control
                    type="text"
                    placeholder="Search videos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      paddingRight: searchQuery ? '35px' : '12px'
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6c757d',
                        fontSize: '16px'
                      }}
                      aria-label="Clear search"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
                {/* Tags Dropdown */}
                <div style={{ width: '200px' }}>
                  <Select
                    isMulti
                    options={availableTags}
                    value={selectedTags}
                    onChange={(newValue) => setSelectedTags(newValue)}
                    placeholder="Filter by tags..."
                    isClearable
                    isSearchable
                    isLoading={loadingTags}
                    maxMenuHeight={300}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '40px',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
                <button className="add-new-btn" onClick={handleAddNew}>
                  Add New +
                </button>
              </div>
            </div>
            
          {/* Tabs */}
          <Nav variant="tabs" className="tournament-tabs mb-4" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'live'}
                onClick={() => setActiveTab('live')}
              >
                Live Videos
              </Nav.Link>
            </Nav.Item>
            {(category === VideoCategory.SOCIAL || category === VideoCategory.LEVEL_UP) && (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'draft'}
                  onClick={() => setActiveTab('draft')}
                >
                  Draft
                </Nav.Link>
              </Nav.Item>
            )}
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'archived'}
                onClick={() => setActiveTab('archived')}
              >
                Archived
              </Nav.Link>
            </Nav.Item>
            {category !== VideoCategory.LEVEL_UP && (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'scheduled'}
                  onClick={() => setActiveTab('scheduled')}
                >
                  Scheduled
                </Nav.Link>
              </Nav.Item>
            )}
            {category !== VideoCategory.LEVEL_UP && (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'rejected'}
                  onClick={() => setActiveTab('rejected')}
                >
                  Rejected
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
          
          {/* Videos Grid */}
          {loading && videos.length === 0 ? (
            <div className="loading-state">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : videos.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <i className="ph-video"></i>
              </div>
              <h3 className="empty-state-title">
                No {activeTab === 'live' ? 'Live' : 
                     activeTab === 'draft' ? 'Draft' :
                     activeTab === 'archived' ? 'Archived' : 
                     activeTab === 'rejected' ? 'Rejected' :
                     'Scheduled'} Videos
              </h3>
              <button className="add-new-btn" onClick={handleAddNew}>
                Add New +
              </button>
            </div>
          ) : (
            <>
              <Row className="community-videos-row">
                {videos.map((video) => {
                  let cardComponent;
                  let colSize;
                  
                  // Determine which action to show based on activeTab
                  // Show "Publish" for draft, scheduled, rejected, and archived tabs
                  // Show "Unpublish" only for live (published) tab
                  // Show "Delete" for all tabs except archived
                  const shouldShowPublish = activeTab === 'draft' || activeTab === 'scheduled' || activeTab === 'rejected' || activeTab === 'archived';
                  const shouldShowUnpublish = activeTab === 'live';
                  const shouldShowDelete = activeTab !== 'archived';
                  
                  if (category === VideoCategory.ACTION) {
                    colSize = { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };
                    cardComponent = (
                      <ActionCard
                        video={video}
                        onEdit={handleEditVideo}
                        onPublish={shouldShowPublish ? handlePublishVideo : undefined}
                        onUnpublish={shouldShowUnpublish ? handleUnpublishVideo : undefined}
                        onDelete={shouldShowDelete ? handleDeleteVideo : undefined}
                        isMenuOpen={openMenuId === video._id}
                        onToggleMenu={() => handleToggleMenu(video._id)}
                        onCloseMenu={handleCloseMenu}
                      />
                    );
                  } else if (category === VideoCategory.LEVEL_UP) {
                    colSize = { xs: 12, sm: 12, md: 4, lg: 4, xl: 4 };
                    // For Level Up series and standalone, don't show Unpublish option - only Edit and Delete
                    const isLevelUpSeries = video.videoType === 'series';
                    const isStandaloneLevelUp = video.videoType === 'single' || (video.contentType === 'long_form' && video.videoType !== 'series' && !video.seriesId);
                    const shouldHideUnpublish = isLevelUpSeries || isStandaloneLevelUp;
                    cardComponent = (
                      <LevelUpCard
                        video={video}
                        onEdit={handleEditVideo}
                        onPublish={shouldShowPublish ? handlePublishVideo : undefined}
                        onUnpublish={shouldHideUnpublish ? undefined : (shouldShowUnpublish ? handleUnpublishVideo : undefined)}
                        onDelete={shouldShowDelete ? handleDeleteVideo : undefined}
                        isMenuOpen={openMenuId === video._id}
                        onToggleMenu={() => handleToggleMenu(video._id)}
                        onCloseMenu={handleCloseMenu}
                      />
                    );
                  } else {
                    colSize = { xs: 12, sm: 6, md: 4, lg: 3 };
                    cardComponent = (
                      <SocialCard
                        video={video}
                        onEdit={handleEditVideo}
                        onPublish={shouldShowPublish ? handlePublishVideo : undefined}
                        onUnpublish={shouldShowUnpublish ? handleUnpublishVideo : undefined}
                        onDelete={shouldShowDelete ? handleDeleteVideo : undefined}
                        isMenuOpen={openMenuId === video._id}
                        onToggleMenu={() => handleToggleMenu(video._id)}
                        onCloseMenu={handleCloseMenu}
                      />
                    );
                  }
                  
                  return (
                    <Col key={video._id} {...colSize} className="mb-4">
                      {cardComponent}
                    </Col>
                  );
                })}
              </Row>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-section mt-4" style={{ 
                  padding: '16px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Page Info */}
                  <div className="page-info mb-3">
                    <span className="text-muted" style={{ 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#6c757d'
                    }}>
                      Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} of {totalCount} videos
                    </span>
                  </div>

                  {/* Pagination Controls */}
                  <div className="pagination-controls d-flex align-items-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1 || loading}
                      className="me-2"
                    >
                      Previous
                    </Button>
                    
                    <div className="page-numbers d-flex align-items-center">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Show pages around current page
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className="mx-1"
                            style={{ minWidth: '40px' }}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages || loading}
                      className="ms-2"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
        
        {/* Add New Form */}
        <AddNewCommunityForm
          show={showAddForm}
          onHide={handleCloseAddForm}
          category={category}
          onSuccess={handleFormSuccess}
        />

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          deleteModalFunction={confirmDeleteVideo}
          title={(videoToDelete?.category === 'level_up' && videoToDelete?.videoType === 'series') ? 'Delete Series' : 'Delete Video'}
          message={videoToDelete 
            ? (videoToDelete.category === 'level_up' && videoToDelete.videoType === 'series'
                ? `Are you sure you want to delete this series "${videoToDelete.title}"? This action cannot be undone.`
                : `Are you sure you want to delete "${videoToDelete.title}"? This action cannot be undone.`)
            : (category === VideoCategory.LEVEL_UP ? 'Are you sure you want to delete this series?' : 'Are you sure you want to delete this video?')}
          confirmLabel={(videoToDelete?.category === 'level_up' && videoToDelete?.videoType === 'series') ? 'Delete Series' : 'Delete Video'}
          cancelLabel="Cancel"
          isLoading={isDeleting}
          error={deleteError}
        />
      </div>
    );
  }
  
  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Community', onClick: () => navigate('/community') },
              { label: categoryLabel, active: true }
            ]} 
          />
        </div>
        
          <div className="community-category-page">
            <div className="category-page-header">
              <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                <button 
                  className="back-button"
                  onClick={() => navigate('/community')}
                  style={{ position: 'relative', zIndex: 10 }}
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <h2 className="category-page-title" style={{ margin: 0 }}>{categoryLabel}</h2>
              </div>
              <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                {/* Search Bar */}
                <div style={{ position: 'relative', width: '250px' }}>
                  <Form.Control
                    type="text"
                    placeholder="Search videos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      height: '40px',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      paddingRight: searchQuery ? '35px' : '12px'
                    }}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6c757d',
                        fontSize: '16px'
                      }}
                      aria-label="Clear search"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
                {/* Tags Dropdown */}
                <div style={{ width: '200px' }}>
                  <Select
                    isMulti
                    options={availableTags}
                    value={selectedTags}
                    onChange={(newValue) => setSelectedTags(newValue)}
                    placeholder="Filter by tags..."
                    isClearable
                    isSearchable
                    isLoading={loadingTags}
                    maxMenuHeight={300}
                    menuPosition="fixed"
                    menuPortalTarget={document.body}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '40px',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999
                      })
                    }}
                  />
                </div>
                <button className="add-new-btn" onClick={handleAddNew}>
                  Add New +
                </button>
              </div>
            </div>
            
          {/* Tabs */}
          <Nav variant="tabs" className="tournament-tabs mb-4" style={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'live'}
                onClick={() => setActiveTab('live')}
              >
                Live Videos
              </Nav.Link>
            </Nav.Item>
            {(category === VideoCategory.SOCIAL || category === VideoCategory.LEVEL_UP) && (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'draft'}
                  onClick={() => setActiveTab('draft')}
                >
                  Draft
                </Nav.Link>
              </Nav.Item>
            )}
            <Nav.Item>
              <Nav.Link 
                className="tournament-tab"
                active={activeTab === 'archived'}
                onClick={() => setActiveTab('archived')}
              >
                Archived
              </Nav.Link>
            </Nav.Item>
            {category !== VideoCategory.LEVEL_UP && (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'scheduled'}
                  onClick={() => setActiveTab('scheduled')}
                >
                  Scheduled
                </Nav.Link>
              </Nav.Item>
            )}
            {category !== VideoCategory.LEVEL_UP && (
              <Nav.Item>
                <Nav.Link 
                  className="tournament-tab"
                  active={activeTab === 'rejected'}
                  onClick={() => setActiveTab('rejected')}
                >
                  Rejected
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
          
          {/* Videos Grid */}
          {loading && videos.length === 0 ? (
            <div className="loading-state">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <Row className="community-videos-row">
                {videos.map((video) => {
                  let cardComponent;
                  let colSize;
                  
                  // Determine which action to show based on activeTab
                  // Show "Publish" for draft, scheduled, rejected, and archived tabs
                  // Show "Unpublish" only for live (published) tab
                  // Show "Delete" for all tabs except archived
                  const shouldShowPublish = activeTab === 'draft' || activeTab === 'scheduled' || activeTab === 'rejected' || activeTab === 'archived';
                  const shouldShowUnpublish = activeTab === 'live';
                  const shouldShowDelete = activeTab !== 'archived';
                  
                  if (category === VideoCategory.ACTION) {
                    colSize = { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };
                    cardComponent = (
                      <ActionCard
                        video={video}
                        onEdit={handleEditVideo}
                        onPublish={shouldShowPublish ? handlePublishVideo : undefined}
                        onUnpublish={shouldShowUnpublish ? handleUnpublishVideo : undefined}
                        onDelete={shouldShowDelete ? handleDeleteVideo : undefined}
                        isMenuOpen={openMenuId === video._id}
                        onToggleMenu={() => handleToggleMenu(video._id)}
                        onCloseMenu={handleCloseMenu}
                      />
                    );
                  } else if (category === VideoCategory.LEVEL_UP) {
                    colSize = { xs: 12, sm: 12, md: 4, lg: 4, xl: 4 };
                    // For Level Up series and standalone, don't show Unpublish option - only Edit and Delete
                    const isLevelUpSeries = video.videoType === 'series';
                    const isStandaloneLevelUp = video.videoType === 'single' || (video.contentType === 'long_form' && video.videoType !== 'series' && !video.seriesId);
                    const shouldHideUnpublish = isLevelUpSeries || isStandaloneLevelUp;
                    cardComponent = (
                      <LevelUpCard
                        video={video}
                        onEdit={handleEditVideo}
                        onPublish={shouldShowPublish ? handlePublishVideo : undefined}
                        onUnpublish={shouldHideUnpublish ? undefined : (shouldShowUnpublish ? handleUnpublishVideo : undefined)}
                        onDelete={shouldShowDelete ? handleDeleteVideo : undefined}
                        isMenuOpen={openMenuId === video._id}
                        onToggleMenu={() => handleToggleMenu(video._id)}
                        onCloseMenu={handleCloseMenu}
                      />
                    );
                  } else {
                    colSize = { xs: 12, sm: 6, md: 4, lg: 3 };
                    cardComponent = (
                      <SocialCard
                        video={video}
                        onEdit={handleEditVideo}
                        onPublish={shouldShowPublish ? handlePublishVideo : undefined}
                        onUnpublish={shouldShowUnpublish ? handleUnpublishVideo : undefined}
                        onDelete={shouldShowDelete ? handleDeleteVideo : undefined}
                        isMenuOpen={openMenuId === video._id}
                        onToggleMenu={() => handleToggleMenu(video._id)}
                        onCloseMenu={handleCloseMenu}
                      />
                    );
                  }
                  
                  return (
                    <Col key={video._id} {...colSize} className="mb-4">
                      {cardComponent}
                    </Col>
                  );
                })}
              </Row>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-section mt-4" style={{ 
                  padding: '16px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {/* Page Info */}
                  <div className="page-info mb-3">
                    <span className="text-muted" style={{ 
                      fontSize: '14px', 
                      fontWeight: '500',
                      color: '#6c757d'
                    }}>
                      Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} of {totalCount} videos
                    </span>
                  </div>

                  {/* Pagination Controls */}
                  <div className="pagination-controls d-flex align-items-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1 || loading}
                      className="me-2"
                    >
                      Previous
                    </Button>
                    
                    <div className="page-numbers d-flex align-items-center">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        // Show pages around current page
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? "primary" : "outline-primary"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className="mx-1"
                            style={{ minWidth: '40px' }}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages || loading}
                      className="ms-2"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
        
      {/* Add New Form / Edit Form */}
      <AddNewCommunityForm
        show={showAddForm}
        onHide={handleCloseAddForm}
        category={category}
        onSuccess={handleFormSuccess}
        video={editingVideo || undefined}
        mode={editingVideo ? 'edit' : 'create'}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        deleteModalFunction={confirmDeleteVideo}
        title="Delete Video"
        message={videoToDelete ? `Are you sure you want to delete "${videoToDelete.title}"? This action cannot be undone.` : 'Are you sure you want to delete this video?'}
        confirmLabel="Delete Video"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        error={deleteError}
      />

      {/* Publish Confirmation Modal */}
      <Modal show={showPublishModal} onHide={() => setShowPublishModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ph-eye me-2 text-primary"></i>
            Publish Video
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="ph-eye text-primary" style={{ fontSize: '48px' }}></i>
            </div>
            <p className="mb-0">
              Are you sure you want to publish <strong>{videoToPublish?.title || 'this video'}</strong>? 
              This will make it visible to all users.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowPublishModal(false);
              setVideoToPublish(null);
            }}
            disabled={isPublishing}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmPublishVideo}
            disabled={isPublishing}
          >
            {isPublishing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Publishing...
              </>
            ) : (
              <>
                <i className="ph-eye me-2"></i>
                Yes, Publish
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Unpublish Confirmation Modal */}
      <Modal show={showUnpublishModal} onHide={() => setShowUnpublishModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="ph-eye-slash me-2 text-warning"></i>
            Unpublish Video
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <div className="mb-3">
              <i className="ph-eye-slash text-warning" style={{ fontSize: '48px' }}></i>
            </div>
            <p className="mb-0">
              Are you sure you want to unpublish <strong>{videoToUnpublish?.title || 'this video'}</strong>? 
              This will hide it from users.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowUnpublishModal(false);
              setVideoToUnpublish(null);
            }}
            disabled={isUnpublishing}
          >
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={confirmUnpublishVideo}
            disabled={isUnpublishing}
          >
            {isUnpublishing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Unpublishing...
              </>
            ) : (
              <>
                <i className="ph-eye-slash me-2"></i>
                Yes, Unpublish
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CommunityCategoryPage;

